import { useState, useEffect } from "react";
import axios from "axios";
import {
  Button,
  OutlineA,
  SubtleIconA,
  SubtleIconButton,
} from "components/button";
import Field from "components/field";
import Assignee from "components/react-select/assignees";
import SafeArea from "components/safe-area";
import { useSnackbarContext } from "components/snackbar";
import Spinner from "components/spinner";
import { useFormik } from "formik";
import Switch from "rc-switch";
import * as React from "react";
import { RiCloseLine } from "react-icons/ri";
import { useMutation, useQuery } from "react-query";
import { useHistory, useParams } from "react-router-dom";
import { TCustomDoc, TCustomDocForm } from "types/custom-doc";
import * as Yup from "yup";
import { SliderPicker } from "react-color";
import { FaArrowCircleUp, FaArrowCircleDown, FaInfoCircle, FaEye, FaEyeSlash } from "react-icons/fa";
import { RiAddCircleLine } from "react-icons/ri";
import { useTranslation } from 'react-i18next';
import TooltippedElement from "components/TooltippedElement";
import { FaRegImage } from "react-icons/fa";
import { BsFileText } from "react-icons/bs";

// Import images from src/images
import partImg from "images/part.png";
import serviceImg from "images/service.png";

// Import custom components
import ImageBox from "./components/ImageBox";
import InteractableLineItem from "./components/InteractableLineItem";
import LineItemPreview from "./components/LineItemPreview";

export default function CustomDocument() {
  const { t } = useTranslation();
  const tBase = "views.custom_document.index2";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const docTypes = [
    { value: "estimate", label: tr("Estimate") },
    { value: "invoice", label: tr("Invoice") },
  ];

  const [, setColorMsg] = useState<string>("");
  const [lastColor, setLastColor] = useState<string | null>(null);
  const [showLineItemsPreview, setShowLineItemsPreview] = useState<boolean>(false);
  const [daysUntilDueWhenEnabled, setDaysUntilDueWhenEnabled] = useState<number | null>(null);
  
  const params = useParams<{ id: string }>(),
    history = useHistory(),
    snackbar = useSnackbarContext();

  const query = useQuery<TCustomDoc>(
    ["custom document", params.id],
    async () =>
      params.id !== "new" &&
      (await axios
        .get(
          `
  ${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/custom-doc/${params.id}
  `
        )
        .then((res) => res.data)),
    {
      refetchOnWindowFocus: false,
      onSuccess: () => {
        formik.resetForm();
      },
    }
  );

  const mutation = useMutation(
    async (e: TCustomDocForm) =>
      await axios
        .post(
          `
  ${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/custom-doc/${params.id}
  `,
          e
        )
        .then((res) => res.data),
    {
      onSuccess: (id) => {
        query.refetch();
        snackbar.showSnackbar(
          params.id === "new"
            ? tr("Custom document created!")
            : tr("Custom document updated!")
        );
        history.push(`/custom-docs/${id}`);
      },
    }
  );

  const initialValues: TCustomDocForm = {
      label: query.data?.label ?? "",
      products: query.data?.products ?? true,
      services: query.data?.services ?? true,
      terms_conditions: query.data?.terms_conditions ?? "",
      additional_terms: query.data?.additional_terms ?? [],
      type: query.data?.type ?? "estimate",
      days_until_due: query.data?.days_until_due ?? null,
      total_only: query.data?.total_only ?? false,
      checklists: query.data?.checklists || [],
      long_description: query.data?.long_description ?? true,
      short_description: query.data?.short_description ?? true,
      name: query.data?.name ?? true,
      assignees: [1],
      color: query.data?.color ?? null,
      include_images: query.data?.include_images ?? false,
      include_qty: query.data?.include_qty ?? false,
      include_line_total: query.data?.include_line_total ?? false,
      include_custom_fields: query.data?.include_custom_fields ?? true,
    },
    onSubmit = (e: TCustomDocForm) => {
      mutation.mutate(e);
    },
    validationSchema = Yup.object({
      label: Yup.string().required(tr("Required")),
      type: Yup.string().required(tr("Required")),
      products: Yup.boolean().required(tr("Required")),
      services: Yup.boolean().required(tr("Required")),
      name: Yup.boolean().required(tr("Required")),
      terms_conditions: Yup.string().nullable(),
      additional_terms: Yup.array().of(
        Yup.object().shape({
          id: Yup.string().required(tr("Required")),
          label: Yup.string().required(tr("Required")),
          text: Yup.string().required(tr("Required")),
          type: Yup.string().required(tr("Required")).oneOf([
            "none",
            "yes_no",
            "initials",
            "yes_no_initials",
          ] as const),
          // order: Yup.number().optional(),
        })
      ),
      days_until_due: Yup.number().min(0).nullable(),
      total_only: Yup.boolean(),
      checklists: Yup.array().of(
        Yup.object().shape({
          id: Yup.string().required(tr("Required")),
          label: Yup.string().required(tr("Required")),
        })
      ),
      long_description: Yup.boolean().required(tr("Required")),
      short_description: Yup.boolean().required(tr("Required")),
      color: Yup.string().nullable(),
      include_images: Yup.boolean().required(tr("Required")),
      include_qty: Yup.boolean().required(tr("Required")),
      include_line_total: Yup.boolean().required(tr("Required")),
    });

  const formik = useFormik<TCustomDocForm>({
    initialValues,
    onSubmit,
    validationSchema,
    enableReinitialize: true,
  });

  useEffect(() => {
    if (formik.values.days_until_due !== null) {
      setDaysUntilDueWhenEnabled(formik.values.days_until_due);
    }
  }, [formik.values.days_until_due]);

  if (query.isLoading) return <Spinner />;

  const populateChecklists = formik.values.checklists?.map((checklist, i) => {
    return (
      <div key={i} className="flex">
        <Field
          value={checklist.label}
          name={`checklists.${i}.label`}
          formik={formik}
          className="flex-1"
        />
        <SubtleIconButton
          className="!text-red-600"
          onClick={() => {
            let temp = [...formik.values.checklists!];
            temp.splice(i, 1);
            formik.setFieldValue("checklists", temp);
          }}
        >
          <RiCloseLine />
        </SubtleIconButton>
      </div>
    );
  });

  const additionalTermTypes = [
    { value: "none", label: tr("None") },
    { value: "yes_no", label: tr("Yes / No") },
    { value: "initials", label: tr("Initials") },
    { value: "yes_no_initials", label: tr("Yes / No + Initials") },
  ];

  const populateAdditionalTerms = formik.values.additional_terms?.map((term, i) => {
    return (
      <div key={i} className="space-y-1 border border-gray-300 p-2 rounded-md bg-slate-50">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-4">
            <Field
              value={term.label}
              name={`additional_terms.${i}.label`}
              formik={formik}
              label={tr("Label")}
              className="w-[200px]"
            />
          <Field
            type="select"
            value={additionalTermTypes.find((option) => option.value === term.type)}
            name={`additional_terms.${i}.type`}
            formik={formik}
            label={tr("Type")}
            className="w-[200px]"
            options={additionalTermTypes}
            onChange={(selectedOption: any) => {
              formik.setFieldValue(`additional_terms.${i}.type`, selectedOption.value);
            }}
          />
          </div>
          <div className="flex items-center">
            <button
              type="button"
              className={`
                text-gray-500 rounded-full py-0.5 pl-0.5 pr-[1.9px] 
                ${i === 0 ? "opacity-50" : "hover:text-white hover:bg-gray-500 active:text-gray-100 hover:shadow-md active:shadow-none"}
              `}
              onClick={() => {
                if (i > 0) {
                  const temp = [...formik.values.additional_terms];
                  [temp[i], temp[i - 1]] = [temp[i - 1], temp[i]];
                  formik.setFieldValue("additional_terms", temp);
                }
              }}
              disabled={i === 0}
            >
              <FaArrowCircleUp size={24} />
            </button>
            <button
              type="button"
              className={`ml-1 mr-6
                text-gray-500 rounded-full py-0.5 pl-0.5 pr-[1.9px] 
                ${i === formik.values.additional_terms.length - 1 ? "opacity-50" : "hover:text-white hover:bg-gray-500 active:text-gray-100 hover:shadow-md active:shadow-none"}
              `}
              onClick={() => {
                if (i < formik.values.additional_terms.length - 1) {
                  const temp = [...formik.values.additional_terms];
                  [temp[i], temp[i + 1]] = [temp[i + 1], temp[i]];
                  formik.setFieldValue("additional_terms", temp);
                }
              }}
              disabled={i === formik.values.additional_terms.length - 1}
            >
              <FaArrowCircleDown size={24} />
            </button>
            <button
              className="text-red-600 hover:scale-[1.1]"
              type="button"
              onClick={() => {
                let temp = [...formik.values.additional_terms!];
                temp.splice(i, 1);
                formik.setFieldValue("additional_terms", temp);
              }}
            >
              <RiCloseLine size={24} />
            </button>
          </div>
        </div>
        <Field
          type="textarea"
          value={term.text}
          name={`additional_terms.${i}.text`}
          formik={formik}
          label={tr("Text")}
          minRows={2}
        />
      </div>
    );
  });

  const handleColorChange = (color: any) => {
    formik.setFieldValue("color", color.hex);
    setLastColor(color.hex); // Update lastColor only when a color is picked
    setColorMsg("");
  };

  const handleDefaultColorSelection = (e: any) => {
    const useDefault = e.target.checked;
    if (useDefault) {
      formik.setFieldValue("color", null);
      setColorMsg(tr("You have selected the default color"));
    } else if (lastColor) {
      formik.setFieldValue("color", lastColor); // Restore the last selected color
      setColorMsg("");
    }
  };

  const getLineItemConfig = () => {
    const handleOnClick = (field: keyof TCustomDocForm) => {
      formik.setFieldValue(field, !formik.values[field]);
    };    

    return [
      { colSpan: 1, label: tr("Images"), node: <FaRegImage size={16} className="my-[3px]"/>,
        value: formik.values.include_images, onClick: () => handleOnClick('include_images'), },
      { colSpan: 2, label: tr("Label"), text: "Part",
        value: formik.values.name, onClick: () => handleOnClick('name') },
      { colSpan: 2, label: tr("Short Description"), text: "Part #1",
        value: formik.values.short_description, onClick: () => handleOnClick('short_description') },
      { colSpan: 3, label: tr("Long Description"), text: "Part #1 w/ Attachments",
        value: formik.values.long_description, onClick: () => handleOnClick('long_description') },
      { colSpan: 2, label: tr("(Qty) x Rate"), text: "(1) x $100.00",
        value: formik.values.include_qty, onClick: () => handleOnClick('include_qty') },
      { colSpan: 2, label: tr("Total"), text: "$100.00",
        value: formik.values.include_line_total, onClick: () => handleOnClick('include_line_total') },
    ];
  };

  return (
    <div className="flex flex-col mb-16">
      <div className="sticky border-b top-0 height-[50px] z-50 bg-white flex p-2 justify-between items-center">
        <div className="space-x-4 flex items-center">
          <SubtleIconA to="/admin/custom-docs">
            <RiCloseLine />
          </SubtleIconA>
          <span>
            {params.id === "new"
              ? tr("Create Custom Document")
              : `${tr("Edit Custom Document")} #${params.id}`}
          </span>
        </div>

        <div className="space-x-2">
          <Button
            primary
            type="submit"
            isLoading={mutation.isLoading}
            onClick={() => formik.handleSubmit()}
            disabled={!formik.dirty || !formik.isValid}
          >
            {params.id === "new"
              ? tr("Create Custom Document")
              : tr("Save Changes")}
          </Button>
          <OutlineA primary to="/admin/custom-docs">
            {tr("Cancel")}
          </OutlineA>
        </div>
      </div>
      <SafeArea>
        <form onSubmit={formik.handleSubmit} className="space-y-4 py-4">
          <Field
            label={tr("Label")}
            name="label"
            type="input"
            formik={formik}
          />
          <Field
            type="select"
            label={tr("Type")}
            onChange={(e: any) => {
              formik.setValues((v) => ({ ...v, type: e.value }));
            }}
            value={docTypes.find((doc) => doc.value === formik.values.type)}
            name="type"
            options={docTypes}
          />
          {formik.values.type === "invoice" && (
            <div>
              <div className="flex items-center space-x-2">
                <div>{tr("Days Until Due")}</div>
                <TooltippedElement
                  message={tr(
                    "Number of days until the invoice is due from send date"
                  )}
                  placement="top"
                  element={<FaInfoCircle className="text-secondary" />}
                />
              </div>
              <div className="flex items-center space-x-4 mt-2 -mb-2">
                <div className="flex flex-col items-center">
                  <div className="text-xs text-gray-600">
                    {formik.values.days_until_due !== null ? (
                      <span>{tr("On")}</span>
                    ) : (
                      <span>{tr("Off")}</span>
                    )}
                  </div>
                  <Switch
                    checked={formik.values.days_until_due !== null}
                    onChange={() => {
                      if (formik.values.days_until_due !== null) {
                        setDaysUntilDueWhenEnabled(formik.values.days_until_due);
                        formik.setFieldValue("days_until_due", null);
                      } else {
                        formik.setFieldValue("days_until_due", daysUntilDueWhenEnabled ?? 30);
                      }
                    }}
                  />
                </div>
                <Field
                  type="number"
                  name="days_until_due"
                  formik={formik}
                  disabled={formik.values.days_until_due === null}
                  placeholder={formik.values.days_until_due === null ? "30" : undefined}
                />
              </div>
            </div>
          )}

          {/* Custom Fields */}
          {formik.values.type === "estimate" && (
            <div className="space-y-2">
              <h3>{tr("Custom Fields")}</h3>
              <ImageBox
                key="custom_fields"
                label={tr("Show Custom Fields")}
                value={formik.values.include_custom_fields}
                onClick={() =>
                  formik.setFieldValue(
                    "include_custom_fields", !formik.values.include_custom_fields
                  )
                }
                img={<BsFileText className="w-8 h-8 mb-1" />}
              />
            </div>
          )}

          {/* Line Items */}
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <h3>{tr("Line Items")}</h3>
              <TooltippedElement
                className="mt-1.5"
                message={tr("Preview Line Items on PDF")}
                placement="top"
                element={
                  <button 
                    onClick={(e) => {
                      e.preventDefault();
                      setShowLineItemsPreview(!showLineItemsPreview)
                    }}
                    className="text-blue-500"
                  >
                    {showLineItemsPreview ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
                  </button>
                }
              />
            </div>
            <div className="flex space-x-2 items-center">
              <Switch
                checked={formik.values.total_only}
                onChange={(e) => {
                  formik.setFieldValue("total_only", e);
                }}
              />
              <div className="text-sm font-semibold text-gray-700">
                {tr("Show Total Only")}
              </div>
            </div>
          </div>
          {!formik.values.total_only && (
            <>
              {/* Parts and Services */}
              <div className="flex space-x-4 my-3">
                <ImageBox
                  key="products"
                  label={tr("Show Parts")}
                  value={formik.values.products}
                  onClick={() =>
                    formik.setFieldValue("products", !formik.values.products)
                  }
                  img={
                    <img src={partImg} alt="Part" className="w-8 h-8 mb-1" />
                  }
                />
                <ImageBox
                  key="services"
                  label={tr("Show Services")}
                  value={formik.values.services}
                  onClick={() =>
                    formik.setFieldValue("services", !formik.values.services)
                  }
                  img={
                    <img
                      src={serviceImg}
                      alt="Service"
                      className="w-8 h-8 mb-1"
                    />
                  }
                />
              </div>
              <div className="text-xs text-gray-500 space-y-1">
                <div>• {tr("Click any section to toggle its visibility")}</div>
                <div>
                  • {tr("Grayed out sections will be hidden in the PDF and email to customer")}
                </div>
              </div>

              {/* Line Items */}
              <InteractableLineItem config={getLineItemConfig()} />
            </>
          )}
          {showLineItemsPreview && (
            <LineItemPreview data={formik.values} />
          )}

          {/* Color */}
          <div className="space-y-2">
            <div className="flex items-center">
              <h3>{tr("Color")}</h3>
              <div className="ml-2 text-gray-500 text-[13px]">
                ({tr("Select a color that best represents your company")})
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <input
                type="checkbox"
                name="color"
                checked={formik.values.color === null}
                onChange={handleDefaultColorSelection}
              />
              <div className="font-[600] text-[13px]">{tr("Default")}</div>
            </div>
            <div>
              <input
                type="input"
                name="colorHex"
                className="mb-1 border border-gray-300 rounded-md w-20 indent-1 text-gray-600"
                maxLength={7}
                placeholder={tr("Color Hex")}
                value={formik.values.color ?? ""}
                onChange={(e) => {
                  formik.setFieldValue("color", e.target.value);
                  setLastColor(e.target.value);
                  setColorMsg("");
                }}
              />
            </div>
            <SliderPicker
              color={formik.values.color ?? undefined}
              onChange={handleColorChange}
            />
          </div>

          {/* Assignees */}
          <div>
            <h3 className="mb-2">{tr("Assignees")}</h3>
            <Assignee value={query.data?.assignees} onChange={(e: any) => {}} />
          </div>

          {/* Terms And Conditions */}
          <div className="space-y-2">
            <div className="flex items-center">
              <h3>{tr("Terms And Conditions")}</h3>
              {formik.values.type !== "invoice" && (
              <button
                type="button"
                className="ml-2 text-primary rounded-full hover:text-white hover:bg-primary active:bg-gray-200"
                onClick={() =>
                  formik.setFieldValue(
                    "additional_terms",
                    formik.values.additional_terms
                      ? [...formik.values.additional_terms, { id: "new", label: "", text: "", type: "none" }]
                      : [{ id: "new", label: "", text: "", type: "none" }]
                  )
                }
              >
                <RiAddCircleLine size={24} />
              </button>
              )}
            </div>
            {formik.values.type !== "invoice" && populateAdditionalTerms}
            <Field
              type="textarea"
              name="terms_conditions"
              label={
                formik.values.type !== "invoice"
                  ? tr("Terms And Conditions")
                  : ""
              }
              minRows={10}
              formik={formik}
            />
          </div>
        </form>
      </SafeArea>
    </div>
  );
}
